import { RequirementTaskInternalDataMethod, UniConfigRequirementTaskFulfilmentMethod } from '../config/index.js';
import { CasShieldDBItem } from '../../dao/index.js';
import { DocumentType } from '../enums.js';
import { OfferTaskRoute, documentsPerRoute } from '../route/index.js';
import { DocumentKey } from '../applicant/document-keys.js';
import {
  ApplicantOfferRequirement,
  ApplicantOfferRequirementTask,
  ApplicantOfferRequirementTaskActionStatus,
  ApplicantOfferRequirementTaskLevel,
  ApplicantOfferRequirementTaskStatus,
} from './enums.js';
import { IDocumentFulfilmentData, IInternalDocumentData } from './requirements-schema.js';

export interface RequirementConditionHistory<FulfilmentData = unknown> {
  fulfilmentData?: FulfilmentData;
  conditionText?: IApplicantOfferRequirementTask['conditionText'];
  internalNote?: IApplicantOfferRequirementTask['internalNote'];
}

export interface IApplicantOfferRequirement {
  id: ApplicantOfferRequirement;
  requirementText?: string;
  tasks: Record<IApplicantOfferRequirementTask['id'], IApplicantOfferRequirementTask>;
}

export interface IApplicantOfferRequirementQualification {
  id: string; // Unique identifier for this qualification entry (KSUID). Legacy data uses 'highest'.
  qualificationId?: string; // Which study this qualification maps to (e.g. 'highest', 'previous', or a specific study ID)
  primary?: boolean; // Whether this is the primary academic requirement. Defaults to true for legacy data.
  requirementText?: string; // Display name for this specific requirement (e.g. "GCSE Science at B or above")
  tasks: Record<IApplicantOfferRequirementTask['id'], IApplicantOfferRequirementTask>;
}

export interface IApplicantOfferAcademicRequirement {
  id: ApplicantOfferRequirement.AcademicRequirement;
  requirementText?: string;
  confirmation?: string;
  qualifications: Record<IApplicantOfferRequirementQualification['id'], IApplicantOfferRequirementQualification>;
}

export interface IApplicantOfferRequirementTask<FulfilmentData = unknown> {
  id: string;
  taskText: string; // The text displayed before a task has been fulfilled
  type: ApplicantOfferRequirementTask;
  status: ApplicantOfferRequirementTaskStatus;
  fulfilmentData?: FulfilmentData | null; // Needs to be nullable so it can be unset
  fulfilmentMethod: UniConfigRequirementTaskFulfilmentMethod;
  docType?: DocumentType;
  level: ApplicantOfferRequirementTaskLevel;
  applicantStatus: ApplicantOfferRequirementTaskActionStatus;
  conditionText?: string;
  internalNote?: string | null;
  conditionHistory: Array<RequirementConditionHistory<FulfilmentData>>;
  internalData?: IInternalDocumentData | null;
  internalDataMethod?: RequirementTaskInternalDataMethod;
  lastUpdatedByApplicant?: boolean;
  dismissedCompletion?: boolean;
  pendingPrefill?: boolean;
  pendingChanges: {
    created?: boolean;
    fulfilmentData?: FulfilmentData | null;
    status?: IApplicantOfferRequirementTask['status'];
    conditionText?: IApplicantOfferRequirementTask['conditionText'];
    level?: IApplicantOfferRequirementTask['level'];
  };
}

export type ApplyRequirementConfig = {
  [key in string]: ApplicantOfferRequirementTaskLevel;
};

export interface IApplicantOfferRequirementTaskResponse extends IApplicantOfferRequirementTask {
  fulfilmentData?: IDocumentFulfilmentData;
}

export type ApplicantOfferRequirementTaskResponse = CasShieldDBItem<IApplicantOfferRequirementTaskResponse>;

export const ApplyRequirementIdMapping: Partial<Record<ApplicantOfferRequirementTask, OfferTaskRoute>> = {
  [ApplicantOfferRequirementTask.Passport]: OfferTaskRoute.Passport,
  [ApplicantOfferRequirementTask.PhotoID]: OfferTaskRoute.PhotoID,
  [ApplicantOfferRequirementTask.AcademicResults]: OfferTaskRoute.AcademicResults,
  [ApplicantOfferRequirementTask.FinalAcademicResults]: OfferTaskRoute.FinalAcademicResults,
  [ApplicantOfferRequirementTask.AcademicCertificate]: OfferTaskRoute.AcademicCertificate,
  [ApplicantOfferRequirementTask.AcademicTranscript]: OfferTaskRoute.AcademicTranscript,
  [ApplicantOfferRequirementTask.EnglishLanguageResults]: OfferTaskRoute.EnglishLanguageResults,
  [ApplicantOfferRequirementTask.EnglishLanguageCertificate]: OfferTaskRoute.EnglishLanguageCertificate,
  [ApplicantOfferRequirementTask.CV]: OfferTaskRoute.CV,
  [ApplicantOfferRequirementTask.ProofOfManagerialExperience]: OfferTaskRoute.ProofOfManagerialExperience,
  [ApplicantOfferRequirementTask.ProofOfAddress]: OfferTaskRoute.ProofOfAddress,
  [ApplicantOfferRequirementTask.ProofOfNameChange]: OfferTaskRoute.ProofOfNameChange,
  [ApplicantOfferRequirementTask.NameAffidavit]: OfferTaskRoute.NameAffidavit,
  [ApplicantOfferRequirementTask.BirthCert]: OfferTaskRoute.BirthCert,
  [ApplicantOfferRequirementTask.ProofOfImmigrationStatus]: OfferTaskRoute.ProofOfImmigrationStatus,
  [ApplicantOfferRequirementTask.Payment]: OfferTaskRoute.RequirementPayment,
  [ApplicantOfferRequirementTask.ProofOfPayment]: OfferTaskRoute.RequirementProofOfPayment,
  [ApplicantOfferRequirementTask.LetterOfRecommendation]: OfferTaskRoute.LetterOfRecommendation,
  [ApplicantOfferRequirementTask.PersonalStatement]: OfferTaskRoute.PersonalStatement,
  [ApplicantOfferRequirementTask.ResearchProposal]: OfferTaskRoute.ResearchProposal,
  [ApplicantOfferRequirementTask.Portfolio]: OfferTaskRoute.Portfolio,
  [ApplicantOfferRequirementTask.RefereeDetails]: OfferTaskRoute.RefereeDetails,
  [ApplicantOfferRequirementTask.Custom]: OfferTaskRoute.Custom,
};

/**
 * Allows looking up requirement ID, route, doc type, etc. by the URL slug.
 * Extracts the last path segment from each OfferTaskRoute and maps it to
 * the corresponding requirementId, route, and docType.
 */
export interface RequirementSlugMapping {
  requirementId: ApplicantOfferRequirementTask;
  route: OfferTaskRoute;
  docType: DocumentType;
  docKey: string;
}

/**
 * Builds a lookup from URL slug to requirement metadata (requirementId, route, docType, docKey).
 *
 * Only entries that have a documentsPerRoute mapping are included. Tasks like AcademicResults,
 * EnglishLanguageResults, and RefereeDetails intentionally have no documentsPerRoute entry and
 * retain their own explicit routes in routes.ts — they will not appear in this map.
 * If one of those explicit routes is removed, the dynamic catch-all will NOT pick it up
 * unless a documentsPerRoute entry is also added.
 *
 * Some slugs produced here (e.g. 'passport') may be shadowed at runtime by named routes
 * that take priority over the dynamic catch-all. Those entries are harmless but unreachable.
 *
 * Duplicate slugs are possible when multiple enum values map to the same route
 * (e.g. ConditionalOffer and UnconditionalOffer both resolve to 'offer-acceptance').
 * These are typically skipped because they have no documentsPerRoute entry, but if one is
 * added a duplicate-slug guard throws at startup so the conflict is surfaced immediately.
 */
function buildRequirementSlugMap(): Record<string, RequirementSlugMapping> {
  const map: Record<string, RequirementSlugMapping> = {};

  for (const [taskKey, route] of Object.entries(ApplyRequirementIdMapping)) {
    if (!route) {
      continue;
    }

    const slug = route.split('/').pop();

    if (!slug || slug.includes('{')) {
      continue;
    }

    const docTypes = documentsPerRoute[route];

    if (!docTypes || docTypes.length === 0) {
      continue;
    }

    const [docType] = docTypes;

    if (map[slug]) {
      throw new Error(
        `Duplicate requirement slug mapping detected for "${slug}": ` +
          `existing=${map[slug].requirementId} (${map[slug].route}), ` +
          `new=${taskKey} (${route})`,
      );
    }

    map[slug] = {
      requirementId: taskKey as ApplicantOfferRequirementTask,
      route,
      docType,
      docKey: (DocumentKey as Record<string, string>)[docType] ?? `${slug}-1`,
    };
  }

  return map;
}

export const requirementSlugMap = buildRequirementSlugMap();
